/**
 * ClientPortalToursMap Hook
 *
 * Renders Google Maps with tour stops and handles bidirectional hover highlighting.
 */

// Tour colors - must stay in sync with @tour_colors in tours_tab.ex
const TOUR_COLORS = ["#0086e6", "#3dc6c3", "#a855f7", "#E11D48", "#8b5cf6"]

export const ClientPortalToursMap = {
  async mounted() {
    this.markersById = new Map()
    this.markers = []
    await this.renderMap()
    this.setupHighlightListeners()
  },

  async updated() {
    await this.renderMap()
  },

  async renderMap() {
    if (this.markers) {
      this.markers.forEach((marker) => marker.setMap(null))
      this.markers = []
    }
    this.markersById.clear()

    const tours = JSON.parse(this.el.dataset.tours || "[]")

    const t = (key) => window.I18n[key]
    const noStops = t("No tour stops with coordinates to display")
    const noCoordinates = t("No tour stops with coordinates")
    const failedToLoadMap = t("Failed to load map")
    const tourTimeLabel = t("Tour time:")

    if (tours.length === 0) {
      this.el.innerHTML = `<div class="flex items-center justify-center h-full text-gray-400">${noStops}</div>`
      return
    }

    try {
      await window.Enaia.loadGoogleMaps()

      const allStops = tours.flatMap((tour) => tour.stops || [])
      const firstStop = allStops.find((s) => s.lat && s.lng)

      if (!firstStop) {
        this.el.innerHTML = `<div class="flex items-center justify-center h-full text-gray-400">${noCoordinates}</div>`
        return
      }

      if (!this.map) {
        this.map = new google.maps.Map(this.el, {
          zoom: 12,
          minZoom: 3,
          backgroundColor: "#e8eef1",
          center: {
            lat: parseFloat(firstStop.lat),
            lng: parseFloat(firstStop.lng),
          },
          mapTypeControl: true,
          mapTypeControlOptions: {
            position: google.maps.ControlPosition.TOP_RIGHT,
            style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
          },
          streetViewControl: true,
          streetViewControlOptions: {
            position: google.maps.ControlPosition.RIGHT_TOP,
          },
          fullscreenControl: true,
          fullscreenControlOptions: {
            position: google.maps.ControlPosition.RIGHT_TOP,
          },
          zoomControl: true,
          zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_CENTER,
          },
          styles: window.Enaia.GoogleMapsStyles.DESATURATED_MAP_STYLES,
        })
      }

      const bounds = new google.maps.LatLngBounds()
      const hook = this

      tours.forEach((tour, tourIdx) => {
        const tourColor = TOUR_COLORS[tourIdx % TOUR_COLORS.length]

        tour.stops.forEach((stop, stopIdx) => {
          if (!stop.lat || !stop.lng) return

          const position = {
            lat: parseFloat(stop.lat),
            lng: parseFloat(stop.lng),
          }

          const tooltipParts = [stop.name]
          if (stop.time) tooltipParts.push(`${tourTimeLabel} ${stop.time}`)

          const defaultIcon = {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: tourColor,
            fillOpacity: 0.9,
            strokeColor: "#ffffff",
            strokeWeight: 2,
            anchor: new google.maps.Point(0, 0),
            labelOrigin: new google.maps.Point(0, 0),
          }

          const highlightedIcon = {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 16,
            fillColor: tourColor,
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 3,
            anchor: new google.maps.Point(0, 0),
            labelOrigin: new google.maps.Point(0, 0),
          }

          const dimmedIcon = {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: tourColor,
            fillOpacity: 0.3,
            strokeColor: "#ffffff",
            strokeWeight: 2,
            anchor: new google.maps.Point(0, 0),
            labelOrigin: new google.maps.Point(0, 0),
          }

          const markerZIndex = tourIdx * 100 + stopIdx + 1

          const marker = new google.maps.Marker({
            position,
            map: this.map,
            title: tooltipParts.join("\n"),
            label: {
              text: `${stopIdx + 1}`,
              color: "#ffffff",
              fontSize: "12px",
              fontWeight: "bold",
            },
            icon: defaultIcon,
            zIndex: markerZIndex,
          })

          this.markers.push(marker)
          bounds.extend(position)

          if (stop.considered_place_id) {
            const stopIdStr = String(stop.considered_place_id)

            hook.markersById.set(stopIdStr, {
              marker,
              color: tourColor,
              defaultIcon,
              highlightedIcon,
              dimmedIcon,
              stopNumber: stopIdx + 1,
              defaultZIndex: markerZIndex,
            })

            marker.addListener("click", () => {
              hook.pushEvent("show_details", { id: stop.considered_place_id })
            })

            marker.addListener("mouseover", () => {
              hook.highlightMarker(stopIdStr)
              window.dispatchEvent(
                new CustomEvent("tours-list:highlight-stop", {
                  detail: { stopId: stop.considered_place_id },
                }),
              )
            })

            marker.addListener("mouseout", () => {
              hook.resetAllMarkers()
              window.dispatchEvent(
                new CustomEvent("tours-list:unhighlight-stop", {
                  detail: { stopId: stop.considered_place_id },
                }),
              )
            })
          }
        })
      })

      if (allStops.length > 1) {
        this.map.fitBounds(bounds)
        const listener = google.maps.event.addListener(this.map, "idle", () => {
          if (this.map.getZoom() > 14) this.map.setZoom(14)
          google.maps.event.removeListener(listener)
        })
      } else if (allStops.length === 1) {
        this.map.setCenter({
          lat: parseFloat(firstStop.lat),
          lng: parseFloat(firstStop.lng),
        })
        this.map.setZoom(14)
      }
    } catch (error) {
      console.error("Error loading Google Maps:", error)
      this.el.innerHTML = `<div class="flex items-center justify-center h-full text-red-500">${failedToLoadMap}</div>`
    }
  },

  highlightMarker(stopId) {
    this.removeGlow()
    this.markersById.forEach((data, id) => {
      if (id === stopId) {
        data.marker.setIcon(data.highlightedIcon)
        data.marker.setZIndex(1000)
        data.marker.setLabel({
          text: `${data.stopNumber}`,
          color: "#ffffff",
          fontSize: "14px",
          fontWeight: "bold",
        })
        this.showGlow(data.marker, data.color)
      } else {
        data.marker.setIcon(data.dimmedIcon)
        data.marker.setZIndex(data.defaultZIndex)
      }
    })
  },

  resetAllMarkers() {
    this.removeGlow()
    this.markersById.forEach((data) => {
      data.marker.setIcon(data.defaultIcon)
      data.marker.setZIndex(data.defaultZIndex)
      data.marker.setLabel({
        text: `${data.stopNumber}`,
        color: "#ffffff",
        fontSize: "12px",
        fontWeight: "bold",
      })
    })
  },

  // Show pulsating glow behind a marker
  showGlow(marker, color) {
    if (!this.glowOverlay) {
      // Inject keyframes once
      if (!document.getElementById("tours-glow-keyframes")) {
        const style = document.createElement("style")
        style.id = "tours-glow-keyframes"
        style.textContent = `
          @keyframes tours-marker-glow {
            0% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
            50% { transform: translate(-50%, -50%) scale(1.8); opacity: 0; }
            100% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
          }
        `
        document.head.appendChild(style)
      }
      this.glowOverlay = new google.maps.OverlayView()
      this.glowOverlay.onAdd = function () {
        this.div = document.createElement("div")
        this.div.style.position = "absolute"
        this.div.style.pointerEvents = "none"
        this.getPanes().overlayLayer.appendChild(this.div)
      }
      this.glowOverlay.draw = function () {
        if (!this.position || !this.div) return
        const projection = this.getProjection()
        const pos = projection.fromLatLngToDivPixel(this.position)
        this.div.style.left = pos.x + "px"
        this.div.style.top = pos.y + "px"
      }
      this.glowOverlay.onRemove = function () {
        if (this.div) {
          this.div.remove()
          this.div = null
        }
      }
      this.glowOverlay.setMap(marker.getMap())
    }
    this.glowOverlay.position = marker.getPosition()
    const overlay = this.glowOverlay
    const updateGlow = () => {
      if (!overlay.div) {
        requestAnimationFrame(updateGlow)
        return
      }
      overlay.div.innerHTML = `<div style="
        width: 40px; height: 40px;
        border-radius: 50%;
        background: ${color};
        position: absolute;
        left: 50%; top: 50%;
        transform: translate(-50%, -50%);
        animation: tours-marker-glow 1.5s ease-in-out infinite;
        filter: blur(4px);
        pointer-events: none;
      "></div>`
      overlay.draw()
    }
    requestAnimationFrame(updateGlow)
  },

  // Remove the glow overlay
  removeGlow() {
    if (this.glowOverlay) {
      this.glowOverlay.setMap(null)
      this.glowOverlay = null
    }
  },

  setupHighlightListeners() {
    const hook = this

    this.highlightHandler = (event) => {
      const stopId = String(event.detail.stopId)
      hook.highlightMarker(stopId)
    }

    this.unhighlightHandler = () => {
      hook.resetAllMarkers()
    }

    window.addEventListener("tours-map:highlight-stop", this.highlightHandler)
    window.addEventListener(
      "tours-map:unhighlight-stop",
      this.unhighlightHandler,
    )
  },

  destroyed() {
    this.removeGlow()
    if (this.highlightHandler) {
      window.removeEventListener(
        "tours-map:highlight-stop",
        this.highlightHandler,
      )
    }
    if (this.unhighlightHandler) {
      window.removeEventListener(
        "tours-map:unhighlight-stop",
        this.unhighlightHandler,
      )
    }
  },
}
